import {
  ReporterPreferences,
  SetPreferencesRequest,
  SetPreferencesResponse,
} from '../types/availability';
import { AuthenticationError } from './AuthenticationError';

const API_BASE_URL = window.APP_CONFIG?.apiUrl || window.location.origin;

// Handle API errors
async function handleApiResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    // Check for authentication error (401)
    if (response.status === 401) {
      throw new AuthenticationError('Your session has expired. Please log in again.');
    }
    
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errorData.error || `API Error: ${response.status}`);
  }
  
  return response.json();
}

export const preferencesApi = {
  
  /**
   * Get reporter's availability preferences
   */
  async getPreferences(): Promise<ReporterPreferences | null> {
    const response = await fetch(`${API_BASE_URL}/api/preferences`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include' // Use cookie-based authentication
    });
    
    if (response.status === 404) {
      // No preferences found, return empty structure
      return null;
    }
    
    return handleApiResponse<ReporterPreferences>(response);
  },

  /**
   * Set reporter's availability preferences
   */
  async setPreferences(request: SetPreferencesRequest): Promise<SetPreferencesResponse> {
    const response = await fetch(`${API_BASE_URL}/api/preferences`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(request)
    });

    if (response.status === 403) {
      const body = await response.json().catch(() => ({ error: 'Preferences locked' }));
      return {
        success: false,
        error: body.error,
        code: body.code,
        editable: false,
        editableUntil: body.editableUntil ?? null,
      };
    }

    return handleApiResponse<SetPreferencesResponse>(response);
  }
};

export default preferencesApi;